import { CoralogixRum } from '@coralogix/browser'
import { UserResponse } from '@descope/core-js-sdk'
import mixpanel from 'mixpanel-browser'
import { PostHog, usePostHog } from 'posthog-js/react'
import { useEffect } from 'react'

import { coralogixUserContext } from '@/lib/config/coralogix.config'

export const useTrackUser = (user: UserResponse, orgId: string) => {
  const posthog = usePostHog()

  useEffect(() => {
    if (user) {
      analyticsTrackUser(posthog, user, orgId)
    } else {
      posthog?.reset()
      mixpanel.reset()
      // Re-register super properties that are cleared by reset
      mixpanel.register({
        env: import.meta.env.MODE,
        'Screen Resolution': `${window.screen.width}x${window.screen.height}`,
      })
    }
  }, [posthog, user, orgId])
}

const analyticsTrackUser = (posthog: PostHog, user: UserResponse, orgId: string) => {
  let roles = user.userTenants?.find((ut) => ut.tenantId === orgId)?.roleNames || []
  // Add the global roles
  roles = roles.concat(user.roleNames || [])

  // PostHog tracking
  posthog?.identify(user.userId, {
    email: user.email,
    name: user.name,
    roles: roles,
    org_ids: user.userTenants?.map((ut) => ut.tenantId),
    orgs: user.userTenants?.map((ut) => ut.tenantName),
  })

  // Mixpanel tracking
  mixpanel.identify(user.userId)
  if (roles.includes('Support User')) {
    mixpanel.stop_session_recording()
  }
  // Re-register super properties that might have been cleared by reset()
  mixpanel.register({
    'Screen Resolution': `${window.screen.width}x${window.screen.height}`,
  })
  mixpanel.people.set({
    $name: user.name || '',
    $email: user.email,
    roles: roles,
    org_ids: user.userTenants?.map((ut) => ut.tenantId),
    orgs: user.userTenants?.map((ut) => ut.tenantName),
    'Screen Resolution': `${window.screen.width}x${window.screen.height}`,
  })

  // Coralogix tracking
  // analyticsTrackUser is expected to be called each time user or org changes so clear before setting new values
  const cgxUserCtx = coralogixUserContext(user)
  CoralogixRum.setUserContext(cgxUserCtx)

  if (user.userTenants && user.userTenants.length >= 1) {
    const tenant = user.userTenants.find((ut) => ut.tenantId === orgId) || user.userTenants[0]

    // PostHog group tracking
    posthog?.group('organization', tenant.tenantId, {
      id: tenant.tenantId,
      env: import.meta.env.MODE,
      name: tenant.tenantName,
    })

    // https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#setting-group-profile-properties
    mixpanel.set_group('organization', tenant.tenantId)
    mixpanel.get_group('organization', tenant.tenantId).set_once({
      $name: tenant.tenantName,
    })

    // Update Coralogix context with org info
    cgxUserCtx.user_metadata = {
      ...cgxUserCtx.user_metadata,
      org_id: tenant.tenantId,
      org_name: tenant.tenantName,
    }
    CoralogixRum.setUserContext(cgxUserCtx)
  }
}
